import { Box, Button, Flex, Select, Tooltip } from "@chakra-ui/react";
import {
  CatalogoDisparo,
  ContextoDisparo,
  TIPO_GATILHO_LABEL,
  TipoGatilho,
  contextosDisponiveis,
  rotuloContexto,
} from "@/models/consignado/disparo.model";
import { DisparosFiltros } from "./DisparosView";

type Props = {
  filtros: DisparosFiltros;
  onChange: (filtros: DisparosFiltros) => void;
  /** Fonte dos contextos do select — o enum só conhece três. */
  catalogo?: CatalogoDisparo;
};

// Recorte rápido por situação — mesmo widget do StatusFilterBar de Vínculos
// Ativos: botões grandes, sólido no selecionado e branco nos demais, para
// aparecerem sobre o fundo colorido do cabeçalho.
const SITUACOES: { valor: boolean | undefined; label: string }[] = [
  { valor: undefined, label: "Todos" },
  { valor: true, label: "Ativos" },
  { valor: false, label: "Inativos" },
];

/**
 * Filtros da listagem de disparos, no cabeçalho.
 *
 * Os três são do servidor: `GET /disparos` aceita `ativo`, `contexto` e
 * `tipoGatilho`. Filtrar no cliente daria número errado, porque a grade é
 * paginada e só tem a página visível em mãos.
 *
 * O gatilho também é alcançável pelas tags de contagem acima da tabela; este
 * select é o controle explícito e o lugar óbvio para voltar a "todos".
 */
export default function DisparosFiltersBar({ filtros, onChange, catalogo }: Props) {
  return (
    <Flex gap={4} width="100%" align="center" flexWrap="wrap">
      <Flex gap={4} flexWrap="wrap">
        {SITUACOES.map((situacao) => {
          const selecionado = filtros.ativo === situacao.valor;
          return (
            <Button
              key={situacao.label}
              size="lg"
              colorScheme="primary"
              variant={selecionado ? "solid" : "outline"}
              bg={selecionado ? undefined : "white"}
              fontFamily="Lexend Deca"
              onClick={() => onChange({ ...filtros, ativo: situacao.valor })}
            >
              {situacao.label}
            </Button>
          );
        })}
      </Flex>

      <Box w={{ base: "100%", md: "320px" }}>
        <Tooltip label="Filtrar por contexto" fontSize="md" placement="bottom">
          <Select
            bg="white"
            height="3rem"
            color="black"
            fontFamily="Lexend Deca"
            fontSize="1rem"
            value={filtros.contexto ?? ""}
            onChange={(e) =>
              onChange({
                ...filtros,
                contexto: (e.target.value || undefined) as ContextoDisparo | undefined,
              })
            }
          >
            <option value="">Todos os contextos</option>
            {contextosDisponiveis(catalogo).map((c) => (
              <option key={c} value={c}>
                {rotuloContexto(c, catalogo)}
              </option>
            ))}
          </Select>
        </Tooltip>
      </Box>

      <Box w={{ base: "100%", md: "260px" }}>
        <Tooltip label="Filtrar por tipo de gatilho" fontSize="md" placement="bottom">
          <Select
            bg="white"
            height="3rem"
            color="black"
            fontFamily="Lexend Deca"
            fontSize="1rem"
            value={filtros.tipoGatilho ?? ""}
            onChange={(e) =>
              onChange({
                ...filtros,
                tipoGatilho: (e.target.value || undefined) as TipoGatilho | undefined,
              })
            }
          >
            <option value="">Todos os gatilhos</option>
            {/* Manual fica oculto, como no formulário; só aparece se já estiver escolhido. */}
            {Object.values(TipoGatilho)
              .filter((t) => t !== TipoGatilho.MANUAL || filtros.tipoGatilho === TipoGatilho.MANUAL)
              .map((t) => (
                <option key={t} value={t}>
                  {TIPO_GATILHO_LABEL[t]}
                </option>
              ))}
          </Select>
        </Tooltip>
      </Box>
    </Flex>
  );
}
